"use client";

import { Dialog, DialogOpenChangeDetails } from "@ark-ui/react/dialog";
import { Portal } from "@ark-ui/react/portal";
import { IconArrowUpRight, IconMinus, IconX } from "@decibeltrade/react-icons";
import { useTranslations } from "next-intl";
import { useCallback, useState } from "react";
import { css, cx } from "styled-system/css";
import { flex, stack } from "styled-system/patterns";

import { Clickable } from "./clickable";
import { Collapsible } from "./collapsible";

export interface FancyNotificationProps {
  /** The title of the notification */
  title: string;
  /** The content of the notification */
  children: React.ReactNode;
  /** Whether the notification dialog is visible */
  visible: boolean;
  /**
   * A callback that's triggered when the notification dialog is dismissed
   * If not provided, the notification will not include a dismiss button
   */
  onDismiss?: () => void;
  /** Whether the notification content can be minimized */
  canMinimize?: boolean;
  /** A class name to apply additional styles to the dialog body */
  className?: string;
}

export function FancyNotificationDialog({
  title,
  children,
  visible,
  onDismiss,
  canMinimize,
  className,
}: FancyNotificationProps) {
  const t = useTranslations("fancyNotification");

  const [isExpanded, setIsExpanded] = useState(true);

  const onOpenChange = useCallback(
    (details: DialogOpenChangeDetails) => {
      if (!details.open && onDismiss) {
        onDismiss();
      }
    },
    [onDismiss],
  );

  const content = (
    <>
      <div className={dividerStyles} />
      {children}
    </>
  );

  return (
    <Dialog.Root
      modal={false}
      closeOnInteractOutside={false}
      closeOnEscape={false}
      lazyMount
      unmountOnExit
      open={visible}
      onOpenChange={onOpenChange}
    >
      <Portal>
        <Dialog.Positioner>
          <Dialog.Content className={dialogStyles}>
            <div className={cx(contentStyles, className)}>
              <div className={titleWrapperStyles}>
                <Dialog.Title
                  className={cx(
                    titleStyles,
                    css({ textAlign: canMinimize || onDismiss ? "left" : "center" }),
                  )}
                >
                  {title}
                </Dialog.Title>
                <div className={flex({ align: "center", gap: "[6px]" })}>
                  {!!canMinimize && (
                    <Clickable
                      ariaLabel={isExpanded ? t("collapse") : t("expand")}
                      onClick={() => setIsExpanded((prev) => !prev)}
                    >
                      {isExpanded ? (
                        <IconMinus className={iconStyles} />
                      ) : (
                        <IconArrowUpRight className={iconStyles} />
                      )}
                    </Clickable>
                  )}
                  {!!onDismiss && (
                    <Dialog.CloseTrigger asChild>
                      <Clickable ariaLabel={t("dismiss")}>
                        <IconX className={iconStyles} />
                      </Clickable>
                    </Dialog.CloseTrigger>
                  )}
                </div>
              </div>

              {canMinimize ? (
                <Collapsible open={isExpanded} onOpenChange={(isOpen) => setIsExpanded(isOpen)}>
                  <Collapsible.Content>{content}</Collapsible.Content>
                </Collapsible>
              ) : (
                content
              )}
            </div>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}

export function FancyNotificationCollapsible({
  title,
  children,
  visible,
  onDismiss,
  canMinimize,
  className,
}: FancyNotificationProps) {
  const t = useTranslations("fancyNotification");

  const [isExpanded, setIsExpanded] = useState(true);

  const content = (
    <>
      <div className={dividerStyles} />
      {children}
    </>
  );

  return (
    <Collapsible open={visible}>
      <Collapsible.Content className={cx(contentStyles, className)}>
        <div className={titleWrapperStyles}>
          <div
            className={cx(
              titleStyles,
              css({ textAlign: canMinimize || onDismiss ? "left" : "center" }),
            )}
          >
            {title}
          </div>
          <div className={flex({ align: "center", gap: "[6px]" })}>
            {!!canMinimize && (
              <Clickable
                ariaLabel={isExpanded ? t("collapse") : t("expand")}
                onClick={() => setIsExpanded((prev) => !prev)}
              >
                {isExpanded ? (
                  <IconMinus className={iconStyles} />
                ) : (
                  <IconArrowUpRight className={iconStyles} />
                )}
              </Clickable>
            )}
            {!!onDismiss && (
              <Clickable ariaLabel={t("dismiss")} onClick={onDismiss}>
                <IconX className={iconStyles} />
              </Clickable>
            )}
          </div>
        </div>

        {canMinimize ? (
          <Collapsible
            open={isExpanded}
            onOpenChange={(isOpen) => setIsExpanded(isOpen)}
            className={css({ w: "full" })}
          >
            <Collapsible.Content>{content}</Collapsible.Content>
          </Collapsible>
        ) : (
          content
        )}
      </Collapsible.Content>
    </Collapsible>
  );
}

export const dialogStyles = css({
  zIndex: "dialog",
  position: "fixed",
  bottom: {
    base: "[calc(env(safe-area-inset-bottom, 0px) + 84px)]",
    desktop: "48",
  },
  left: { base: "0", sm: "16" },
  right: { base: "0", sm: "[auto]" },
  "&[data-state='open']": {
    animationStyle: "slide-in-from-bottom",
  },
  "&[data-state='closed']": {
    animationStyle: "slide-out-to-bottom",
  },
});

const contentStyles = stack({
  alignItems: "center",
  gap: "0",
  w: "[calc(100vw - 32px)]",
  maxW: "[356px]",
  mx: "auto",
  bg: "transparent",
  bgImage: "unset",
  rounded: "300",
  shadow: "light",
  overflow: "hidden!",
  position: "relative",
  _before: {
    content: '""',
    position: "absolute",
    zIndex: "[-2]",
    minW: "full",
    minH: "full",
    left: "[50%]",
    top: "[50%]",
    translate: "[-50% -50%]",
    scale: "[2]",
    aspectRatio: "1/1",
    bg: "[linear-gradient(45deg, #1F1F1F 40%, #929200 50%, #1F1F1F 60%)]",
    bgRepeat: "no-repeat",
    animation: "[spin 5s linear infinite]",
  },
  _after: {
    content: '""',
    position: "absolute",
    zIndex: "[-1]",
    top: "1",
    left: "1",
    w: "[calc(100% - 2px)]",
    h: "[calc(100% - 2px)]",
    bgGradient: "modalBackground",
    rounded: "300",
  },
});

const titleWrapperStyles = css({
  w: "full",
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr) auto",
  alignItems: "center",
  px: "12",
  py: "8",
});

const titleStyles = css({ textStyle: "heading.2xs", color: "text.primary" });

const iconStyles = css({ h: "20", w: "20", color: "icon.secondary" });

const dividerStyles = css({
  bg: "transparent",
  bgImage:
    "[linear-gradient(98deg, #1F1F1F 5.39%, #1F1F1F 29.46%, #383838 45.91%, #1F1F1F 69.04%)]",
  h: "1",
  w: "[calc(100% - 2px)]",
});
